import { Routes } from '@angular/router';
import { authGuard } from './auth/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';

export const routes: Routes = [
    {
        path: 'login',
        title: 'Login',
        loadComponent: () => import('./auth/login/login').then(m => m.Login)
    },
    {
        path: 'forgot-password',
        title: 'Forgot Password',
        loadComponent: () => import('./auth/forget-password/forget-password').then(m => m.ForgetPassword)
    },
    {
        path: 'reset-password',
        title: 'Reset Password',
        loadComponent: () => import('./auth/reset-password/reset-password').then(m => m.ResetPassword)
    },
    {
        path: 'accept-invite/:token',
        title: 'Accept Invite',
        loadComponent: () => import('./auth/accept-invite/accept-invite').then(m => m.AcceptInvite)
    },
    {
        path: '2fa/setup',
        title: 'Two-Factor Setup',
        loadComponent: () => import('./auth/two-factor-setup/two-factor-setup').then(m => m.TwoFactorSetup)
    },
    {
        path: '2fa/verify',
        title: 'Two-Factor Verify',
        loadComponent: () => import('./auth/two-factor-verify/two-factor-verify').then(m => m.TwoFactorVerify)
    },
    {
        path: '',
        loadComponent: () => import('./core/layouts/main').then(m => m.Main),
        canActivateChild: [authGuard],
        children: [
            {
                path :'',
                pathMatch: 'full',
                redirectTo: 'dashboard'
            },
            {
                path: 'dashboard',
                title: 'Dashboard',
                loadComponent: () => import('./features/dashboard/dashboard/dashboard').then(m => m.Dashboard)
            },
            {
                path: 'planning',
                title: 'Planning',
                loadComponent: () => import('./features/planning/planning/planning').then(m => m.Planning)
            },
            {
                path: 'roles',
                title: 'Roles',
                loadComponent: () => import('./features/users/roles/roles').then(m => m.Roles)
            },
            {
                path: 'roles/create',
                title: 'Create Role',
                canActivate: [permissionGuard('roles', 'edit')],
                loadComponent: () => import('./features/users/create-role/create-role').then(m => m.CreateRole)
            },
            {
                path: 'roles/:id',
                title: 'Role Detail',
                loadComponent: () => import('./features/users/role-detail/role-detail').then(m => m.RoleDetail)
            },
            {
                path: 'users',
                title: 'Users',
                loadComponent: () => import('./features/users/users/users').then(m => m.Users)
            },
            {
                path: 'users/:id',
                title: 'User Detail',
                loadComponent: () => import('./features/users/user-detail/user-detail').then(m => m.UserDetail)
            },
            {
                path: 'profile',
                title: 'Profile',
                loadComponent: () => import('./features/profile/profile/profile').then(m => m.Profile)
            },
            {
                path: 'edit-profile',
                title: 'Edit Profile',
                loadComponent: () => import('./features/profile/edit-profile/edit-profile').then(m => m.EditProfile)
            },
            {
                path: 'inspections',
                title: 'Inspections',
                loadComponent: () => import('./features/inspections/all-inspections/all-inspections').then(m => m.AllInspections)
            },
            {
                path: 'projects/active',
                title: 'Active Projects',
                loadComponent: () => import('./features/projects/active-projects/active-projects').then(m => m.ActiveProjects)
            },
            {
                path: 'projects/add',
                title: 'Add Project',
                canActivate: [permissionGuard('projects', 'edit')],
                loadComponent: () => import('./features/projects/add-project/add-project').then(m => m.AddProject)
            },
            {
                path: 'projects/details/:internalNumber',
                title: 'Project Details',
                loadComponent: () => import('./features/projects/project-details/project-details').then(m => m.ProjectDetails)
            },
            {
                path: 'projects/settings/:internalNumber',
                title: 'Project Settings',
                loadComponent: () => import('./features/projects/project-settings/project-settings').then(m => m.ProjectSettings)
            },
            {
                path: 'projects/logs/:internalNumber',
                title: 'Project Logs',
                loadComponent: () => import('./features/projects/project-logs/project-logs').then(m => m.ProjectLogs)
            },
            {
                path: 'projects/document/:internalNumber',
                title: 'Project Document',
                loadComponent: () => import('./features/projects/project-document-details/project-document-details').then(m => m.ProjectDocumentDetails)
            },
            {
                path: 'projects/send-document/:internalNumber',
                title: 'Send Document',
                loadComponent: () => import('./features/projects/send-document/send-document').then(m => m.SendDocument)
            },
            {
                path: 'projects/new-inspection/:internalNumber',
                title: 'New Inspection',
                loadComponent: () => import('./features/projects/new-inspection/new-inspection').then(m => m.NewInspection)
            },
            {
                path: 'projects/create-inspection/:internalNumber',
                title: 'Create Inspection',
                loadComponent: () => import('./features/projects/create-inspection/create-inspection').then(m => m.CreateInspection)
            },
            {
                path: 'projects/create-inspection/:internalNumber/:inspectionId',
                title: 'Create Inspection',
                loadComponent: () => import('./features/projects/create-inspection/create-inspection').then(m => m.CreateInspection)
            },
            {
                path: 'projects/send-inspection/:internalNumber/:inspectionId',
                title: 'Send Inspection',
                loadComponent: () => import('./features/projects/send-inspection/send-inspection').then(m => m.SendInspection)
            },
            {
                path: 'projects/closed',
                title: 'Closed Projects',
                loadComponent: () => import('./features/projects/closed-projects/closed-projects').then(m => m.ClosedProjects)
            },
            {
                path: 'projects/closed/:internalNumber',
                title: 'Project Details',
                loadComponent: () => import('./features/projects/project-details/project-details').then(m => m.ProjectDetails)
            },
            {
                path: 'contracts',
                title: 'Contracts',
                loadComponent: () => import('./features/contacts/contracts/contracts').then(m => m.Contracts)
            },
            {
                path: 'contracts/add',
                title: 'Add Contract',
                canActivate: [permissionGuard('contracts', 'edit')],
                loadComponent: () => import('./features/contacts/add-contract/add-contract').then(m => m.AddContract)
            },
            {
                path: 'contracts/:id',
                title: 'Contract Detail',
                loadComponent: () => import('./features/contacts/contract-detail/contract-detail').then(m => m.ContractDetail)
            },
            {
                path: 'contractors',
                title: 'Contractors',
                loadComponent: () => import('./features/contacts/contractors/contractors').then(m => m.Contractors)
            },
            {
                path: 'contractors/add',
                title: 'Add Contractor',
                canActivate: [permissionGuard('contractors', 'edit')],
                loadComponent: () => import('./features/contacts/add-contractor/add-contractor').then(m => m.AddContractor)
            },
            {
                path: 'contractors/:id',
                title: 'Contractor Detail',
                loadComponent: () => import('./features/contacts/contractor-detail/contractor-detail').then(m => m.ContractorDetail)
            },
            {
                path: 'contacten/add',
                title: 'Add Contact',
                canActivate: [permissionGuard('contacts', 'edit')],
                loadComponent: () => import('./features/contacts/add-contact/add-contact').then(m => m.AddContact)
            },
            {
                path: 'contacten',
                title: 'Contacts',
                loadComponent: () => import('./features/contacts/contacten/contacten').then(m => m.Contacten)
            },
            {
                path: 'contacten/:id',
                title: 'Contact Detail',
                loadComponent: () => import('./features/contacts/contact-detail/contact-detail').then(m => m.ContactDetail)
            },
            {
                path: 'clients',
                title: 'Clients',
                loadComponent: () => import('./features/contacts/clients/clients').then(m => m.Clients)
            },
            {
                path: 'clients/add',
                title: 'Add Client',
                canActivate: [permissionGuard('clients', 'edit')],
                loadComponent: () => import('./features/contacts/add-client/add-client').then(m => m.AddClient)
            },
            {
                path: 'clients/:id',
                title: 'Client Detail',
                loadComponent: () => import('./features/contacts/client-detail/client-detail').then(m => m.ClientDetail)
            },
               {
                   path: 'clients/import',
                   title: 'Import Clients',
                   loadComponent: () =>
                       import('./features/contacts/import-clients/import-clients').then((c) => c.ImportClients),
               },
            // Settings - Languages
            {
                path: 'settings/languages',
                title: 'Languages',
                loadComponent: () => import('./features/settings/languages/languages').then(m => m.Languages)
            },
            {
                path: 'settings/languages/:code',
                title: 'Language Detail',
                loadComponent: () => import('./features/settings/language-detail/language-detail').then(m => m.LanguageDetail)
            },
            // Settings - Email Templates
            {
                path: 'settings/email-templates',
                title: 'Email Templates',
                loadComponent: () => import('./features/settings/email-templates/email-templates').then(m => m.EmailTemplates)
            },
            {
                path: 'settings/email-templates/:id',
                title: 'Email Template',
                loadComponent: () => import('./features/settings/email-template-detail/email-template-detail').then(m => m.EmailTemplateDetail)
            },
            // Settings - Documents
            {
                path: 'settings/documents',
                title: 'Documents',
                loadComponent: () => import('./features/settings/documents/documents').then(m => m.Documents)
            },
            {
                path: 'settings/documents/create',
                title: 'Create Document',
                canActivate: [permissionGuard('documents', 'edit')],
                loadComponent: () => import('./features/settings/create-document/create-document').then(m => m.CreateDocument)
            },
            {
                path: 'settings/documents/:id',
                title: 'Document Detail',
                loadComponent: () => import('./features/settings/document-detail/document-detail').then(m => m.DocumentDetail)
            },
            {
                path: 'settings/documents/:id/edit',
                title: 'Edit Document',
                canActivate: [permissionGuard('documents', 'edit')],
                loadComponent: () => import('./features/settings/create-document/create-document').then(m => m.CreateDocument)
            },
            {
                path: 'settings/documents/:id/chapters/new',
                title: 'New Chapter',
                canActivate: [permissionGuard('documents', 'edit')],
                loadComponent: () => import('./features/settings/create-chapter/create-chapter').then(m => m.CreateChapter)
            },
            // Settings - Capacity
            {
                path: 'settings/capacity',
                title: 'Capacity',
                loadComponent: () => import('./features/settings/capacity/capacity').then(m => m.Capacity)
            },
            {
                path: 'settings/capacity/create',
                title: 'Create Capacity',
                loadComponent: () => import('./features/settings/capacity-detail/capacity-detail').then(m => m.CapacityDetail)
            },
            {
                path: 'settings/capacity/:id',
                title: 'Capacity Detail',
                loadComponent: () => import('./features/settings/capacity-detail/capacity-detail').then(m => m.CapacityDetail)
            },
            // Settings - Specialisation
            {
                path: 'settings/specialisation',
                title: 'Specialisation',
                loadComponent: () => import('./features/settings/specialisation/specialisation').then(m => m.Specialisation)
            },
            {
                path: 'settings/specialisation/create',
                title: 'Create Specialisation',
                canActivate: [permissionGuard('specialisation', 'edit')],
                loadComponent: () => import('./features/settings/specialisation-detail/specialisation-detail').then(m => m.SpecialisationDetail)
            },
            {
                path: 'settings/specialisation/:id',
                title: 'Specialisation Detail',
                loadComponent: () => import('./features/settings/specialisation-detail/specialisation-detail').then(m => m.SpecialisationDetail)
            },
            // Settings - Scoresysteem
            {
                path: 'settings/scoresysteem',
                title: 'Score System',
                loadComponent: () => import('./features/settings/scoresysteem/scoresysteem').then(m => m.Scoresysteem)
            },
            {
                path: 'settings/scoresysteem/create',
                title: 'Create Score',
                canActivate: [permissionGuard('scoring-system', 'edit')],
                loadComponent: () => import('./features/settings/scoresysteem-create/scoresysteem-create').then(m => m.ScoresysteemCreate)
            },
            {
                path: 'settings/scoresysteem/:id/edit',
                title: 'Edit Score',
                canActivate: [permissionGuard('scoring-system', 'edit')],
                loadComponent: () => import('./features/settings/scoresysteem-edit/scoresysteem-edit').then(m => m.ScoresysteemEdit)
            },
            // Settings - Impact Levels
            {
                path: 'settings/impact-levels',
                canActivate: [authGuard],
                loadComponent: () => import('./features/settings/impact-levels/impact-levels').then(m => m.ImpactLevels)
            },
            {
                path: 'settings/impact-levels/create',
                canActivate: [permissionGuard('scoring-system', 'edit')],
                loadComponent: () => import('./features/settings/impact-levels-create/impact-levels-create').then(m => m.ImpactLevelsCreate)
            },
            {
                path: 'settings/impact-levels/:id/edit',
                canActivate: [permissionGuard('scoring-system', 'edit')],
                loadComponent: () => import('./features/settings/impact-levels-edit/impact-levels-edit').then(m => m.ImpactLevelsEdit)
            },
            // Settings - Inspections: Checklist
            {
                path: 'settings/inspections/checklist',
                title: 'Checklist',
                loadComponent: () => import('./features/settings/inspections/checklist/checklist').then(m => m.Checklist)
            },
            {
                path: 'settings/inspections/checklist/create',
                title: 'Create Checklist',
                canActivate: [permissionGuard('inspections', 'edit')],
                loadComponent: () => import('./features/settings/inspections/checklist-create/checklist-create').then(m => m.ChecklistCreate)
            },
            {
                path: 'settings/inspections/checklist/:id',
                title: 'Checklist Detail',
                loadComponent: () => import('./features/settings/inspections/checklist-detail/checklist-detail').then(m => m.ChecklistDetail)
            },
            // Settings - Inspections: Chapters
            {
                path: 'settings/inspections/chapters',
                title: 'Inspection Chapters',
                loadComponent: () => import('./features/settings/inspections/chapters/chapters').then(m => m.Chapters)
            },
            {
                path: 'settings/inspections/chapters/create',
                title: 'Create Chapter',
                canActivate: [permissionGuard('inspections', 'edit')],
                loadComponent: () => import('./features/settings/inspections/chapters-create/chapters-create').then(m => m.ChaptersCreate)
            },
            {
                path: 'settings/inspections/chapters/:id',
                title: 'Chapter Detail',
                loadComponent: () => import('./features/settings/inspections/chapters-detail/chapters-detail').then(m => m.ChaptersDetail)
            },
            // Settings - Inspections: Afwijkingen
            {
                path: 'settings/inspections/afwijkingen',
                title: 'Deviations',
                loadComponent: () => import('./features/settings/inspections/afwijkingen/afwijkingen').then(m => m.Afwijkingen)
            },
            {
                path: 'settings/inspections/afwijkingen/create',
                title: 'Create Deviation',
                canActivate: [permissionGuard('inspections', 'edit')],
                loadComponent: () => import('./features/settings/inspections/afwijkingen-create/afwijkingen-create').then(m => m.AfwijkingenCreate)
            },
            {
                path: 'settings/inspections/afwijkingen/:id',
                title: 'Deviation Detail',
                loadComponent: () => import('./features/settings/inspections/afwijkingen-detail/afwijkingen-detail').then(m => m.AfwijkingenDetail)
            },
            // Settings - Inspections: Goedgekeurd
            {
                path: 'settings/inspections/goedgekeurd',
                title: 'Approved',
                loadComponent: () => import('./features/settings/inspections/goedgekeurd/goedgekeurd').then(m => m.Goedgekeurd)
            },
            {
                path: 'settings/inspections/goedgekeurd/create',
                title: 'Create Approved',
                canActivate: [permissionGuard('inspections', 'edit')],
                loadComponent: () => import('./features/settings/inspections/goedgekeurd-create/goedgekeurd-create').then(m => m.GoedgekeurdCreate)
            },
            {
                path: 'settings/inspections/goedgekeurd/:id',
                title: 'Approved Detail',
                loadComponent: () => import('./features/settings/inspections/goedgekeurd-detail/goedgekeurd-detail').then(m => m.GoedgekeurdDetail)
            },
            // Settings - Inspections: Opmerkingen
            {
                path: 'settings/inspections/opmerkingen',
                title: 'Remarks',
                loadComponent: () => import('./features/settings/inspections/opmerkingen/opmerkingen').then(m => m.Opmerkingen)
            },
            {
                path: 'settings/inspections/opmerkingen/create',
                title: 'Create Remark',
                canActivate: [permissionGuard('inspections', 'edit')],
                loadComponent: () => import('./features/settings/inspections/opmerkingen-create/opmerkingen-create').then(m => m.OpmerkingenCreate)
            },
            {
                path: 'settings/inspections/opmerkingen/:id',
                title: 'Remark Detail',
                loadComponent: () => import('./features/settings/inspections/opmerkingen-detail/opmerkingen-detail').then(m => m.OpmerkingenDetail)
            },
            {
                path: 'settings/inspections/hour-types',
                title: 'Hour Types',
                loadComponent: () => import('./features/settings/inspections/hour-types/hour-types').then(m => m.HourTypes)
            },
            {
                path: 'settings/inspections/hour-types/create',
                title: 'Create Hour Type',
                canActivate: [permissionGuard('inspections', 'edit')],
                loadComponent: () => import('./features/settings/inspections/hour-types-create/hour-types-create').then(m => m.HourTypesCreate)
            },
            {
                path: 'settings/inspections/hour-types/:id',
                title: 'Hour Type Detail',
                loadComponent: () => import('./features/settings/inspections/hour-types-detail/hour-types-detail').then(m => m.HourTypesDetail)
            },
            {
                path: 'settings/inspections/standard-text',
                title: 'Standard Text',
                loadComponent: () => import('./features/settings/inspections/standard-text/standard-text').then(m => m.StandardText)
            },
            // Settings - Projecten
            {
                path: 'settings/projecten',
                title: 'Project Settings',
                loadComponent: () => import('./features/settings/projecten-settings/projecten-settings').then(m => m.ProjectenSettings)
            },
            // Settings - Help & Support hub
            {
                path: 'settings/help-support',
                title: 'Help & Support',
                loadComponent: () => import('./features/settings/help-support/help-support').then(m => m.HelpSupport)
            },
            // Settings - Handleiding (manual articles)
            {
                path: 'settings/help-support/handleiding',
                title: 'Manual',
                loadComponent: () => import('./features/settings/help-manual/help-manual').then(m => m.HelpManual)
            },
            {
                path: 'settings/help-support/handleiding/create',
                title: 'Create Article',
                canActivate: [permissionGuard('help', 'edit')],
                loadComponent: () => import('./features/settings/help-support-detail/help-support-detail').then(m => m.HelpSupportDetail)
            },
            {
                path: 'settings/help-support/handleiding/:id',
                title: 'Article Detail',
                loadComponent: () => import('./features/settings/help-support-detail/help-support-detail').then(m => m.HelpSupportDetail)
            },
            // Settings - App Updates
            {
                path: 'settings/help-support/app-updates',
                title: 'App Updates',
                loadComponent: () => import('./features/settings/help-app-updates/help-app-updates').then(m => m.HelpAppUpdates)
            },
            {
                path: 'settings/help-support/app-updates/:id',
                title: 'Update Detail',
                loadComponent: () => import('./features/settings/help-app-updates-detail/help-app-updates-detail').then(m => m.HelpAppUpdatesDetail)
            },
            // Settings - FAQ
            {
                path: 'settings/help-support/faq',
                title: 'FAQ',
                loadComponent: () => import('./features/settings/help-faq/help-faq').then(m => m.HelpFaq)
            },
            {
                path: 'settings/help-support/faq/create',
                title: 'Create FAQ',
                canActivate: [permissionGuard('help', 'edit')],
                loadComponent: () => import('./features/settings/help-faq-detail/help-faq-detail').then(m => m.HelpFaqDetail)
            },
            {
                path: 'settings/help-support/faq/:id',
                title: 'FAQ Detail',
                loadComponent: () => import('./features/settings/help-faq-detail/help-faq-detail').then(m => m.HelpFaqDetail)
            },
            // Help & Ondersteuning (user-facing)
            {
                path: 'help-support',
                title: 'Help & Support',
                loadComponent: () => import('./features/help/help-ondersteuning/help-ondersteuning').then(m => m.HelpOndersteuning)
            },
            {
                path: 'help-support/handleiding',
                title: 'Manual',
                loadComponent: () => import('./features/help/handleiding/handleiding').then(m => m.Handleiding)
            },
            {
                path: 'help-support/handleiding/:id',
                title: 'Manual Detail',
                loadComponent: () => import('./features/help/handleiding-detail/handleiding-detail').then(m => m.HandleidingDetail)
            },
            {
                path: 'help-support/updates',
                title: 'Updates',
                loadComponent: () => import('./features/help/updates-applicatie/updates-applicatie').then(m => m.UpdatesApplicatie)
            },
            {
                path: 'help-support/updates/:id',
                title: 'Update Detail',
                loadComponent: () => import('./features/help/updates-detail/updates-detail').then(m => m.UpdatesDetail)
            },
        ]
    }
];
